import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '@/components/data-display/Badge';
import { Button } from '@/components/common/Button';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { api, type ApiError } from '@/lib/api-client';
import { useAuth } from '@/hooks/useAuth';
import {
  PermissionGrid,
  type PermissionGridPermission,
  type PermissionGridTenant,
  type PermissionGridValue,
} from '@/components/operators/PermissionGrid';
import { usePermission } from '@/hooks/usePermission';
import {
  CREATE_OPERATOR,
  grantableFrom,
  operatorStatus,
  type Operator,
} from '@/features/operators/types';

export type OperatorDetailResponse = Operator;

// ─── Pure helpers (exposed for unit tests) ─────────────────────────────────

/**
 * Build the grid state from the operator's flat permissions list, seeded with
 * every active tenant so the admin can grant on tenants the operator
 * currently has no presence on.
 */
export function buildGridFromOperator(
  tenants: PermissionGridTenant[],
  operatorPermissions: OperatorDetailResponse['permissions'],
): PermissionGridValue {
  const grid: PermissionGridValue = Object.fromEntries(
    tenants.map((t) => [t.tenantCod, [] as string[]]),
  );
  for (const p of operatorPermissions) {
    if (!grid[p.tenantCod]) {
      grid[p.tenantCod] = [];
    }
    grid[p.tenantCod].push(p.permissionCode);
  }
  for (const code of Object.keys(grid)) {
    grid[code] = [...grid[code]].sort();
  }
  return grid;
}

/**
 * Compare two grids and return one PUT-shaped record per tenant row that
 * differs. Both inputs are normalised to sorted-set semantics before
 * comparison so out-of-order arrays don't produce false positives.
 */
export function diffGrids(
  original: PermissionGridValue,
  current: PermissionGridValue,
): Array<{ tenantCod: string; permissionCodes: string[] }> {
  const tenantCods = new Set([...Object.keys(original), ...Object.keys(current)]);
  const changed: Array<{ tenantCod: string; permissionCodes: string[] }> = [];
  for (const tenantCod of tenantCods) {
    const a = [...(original[tenantCod] ?? [])].sort();
    const b = [...(current[tenantCod] ?? [])].sort();
    const equal = a.length === b.length && a.every((v, i) => v === b[i]);
    if (!equal) {
      changed.push({ tenantCod, permissionCodes: current[tenantCod] ?? [] });
    }
  }
  return changed;
}

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'statusCode' in error &&
    typeof (error as ApiError).statusCode === 'number'
  );
}

function describeApiError(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    if (Array.isArray(error.message)) return error.message.join(' · ');
    return error.message;
  }
  return fallback;
}

function formatLastLogin(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

// ─── Component ─────────────────────────────────────────────────────────────

/**
 * Operator-management detail page (D-10, plan §8.2).
 *
 * - Header: identity + active/inactive badge + last login.
 * - Permission grid: reused from D-09, pre-filled from the operator's
 *   current grants. "Save permissions" diffs against the as-loaded state
 *   and issues one PUT per changed tenant row.
 * - Deactivate / Reactivate: PUT /api/users/:username { isActive }. The
 *   deactivate button is disabled with a tooltip when the operator is
 *   the currently-logged-in admin (the backend refuses regardless — D9).
 */
export function OperatorDetailPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const operatorQuery = useQuery<OperatorDetailResponse>({
    queryKey: ['operators', username],
    queryFn: () => api.get<OperatorDetailResponse>(`/api/users/${username}`),
    enabled: !!username,
  });

  const tenantsQuery = useQuery<PermissionGridTenant[]>({
    queryKey: ['admin', 'tenants'],
    queryFn: () => api.get<PermissionGridTenant[]>('/api/admin/tenants'),
  });

  const permissionsQuery = useQuery<PermissionGridPermission[]>({
    queryKey: ['admin', 'permissions'],
    queryFn: () => api.get<PermissionGridPermission[]>('/api/admin/permissions'),
  });

  const operator = operatorQuery.data;
  const tenants = useMemo(() => tenantsQuery.data ?? [], [tenantsQuery.data]);
  const permissions = useMemo(() => permissionsQuery.data ?? [], [permissionsQuery.data]);

  // Mes propres droits, tous tenants : le JWT ne porte que le tenant courant,
  // et « on ne donne que ce que l'on detient » se juge tenant par tenant.
  const meQuery = useQuery<Operator>({
    queryKey: ['operators', currentUser?.username],
    queryFn: () => api.get<Operator>(`/api/users/${currentUser?.username}`),
    enabled: !!currentUser?.username,
  });
  const { has } = usePermission();

  const [grid, setGrid] = useState<PermissionGridValue>({});
  const [originalGrid, setOriginalGrid] = useState<PermissionGridValue>({});
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [resending, setResending] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Seed grid + original once the operator and tenant catalog are loaded.
  useEffect(() => {
    if (operator && tenants.length > 0 && Object.keys(originalGrid).length === 0) {
      const seeded = buildGridFromOperator(tenants, operator.permissions);
      setGrid(seeded);
      setOriginalGrid(seeded);
    }
  }, [operator, tenants, originalGrid]);

  const isSelf = !!currentUser && !!username && currentUser.username === username;
  const pendingChanges = useMemo(() => diffGrids(originalGrid, grid), [originalGrid, grid]);
  const hasPendingChanges = pendingChanges.length > 0;

  // Les trois regles de #149, telles que le serveur les applique — l'ecran
  // les montre avant, pour ne pas proposer un clic qui sera refuse.
  const grantable = useMemo(
    () => (meQuery.data ? grantableFrom(meQuery.data) : {}),
    [meQuery.data],
  );
  const canGrantAnywhere = Object.keys(grantable).length > 0;
  const iCreatedThisOperator =
    !!operator?.createdBy && !!currentUser && operator.createdBy === currentUser.username;
  const gridLocked = savingPermissions || iCreatedThisOperator || !canGrantAnywhere;
  const status = operator ? operatorStatus(operator) : null;
  const canResend = has(CREATE_OPERATOR) && !!operator && !operator.lastLoginAt;

  const onResendInvitation = async () => {
    if (!username) return;
    setServerError(null);
    setResending(true);
    try {
      const updated = await api.post<Operator>(`/api/users/${username}/resend-invitation`, {});
      if (updated.keycloakStatus === 'pending') {
        toast.error('Keycloak could not be reached — the account still does not exist there.');
      } else if (!updated.invitedAt) {
        toast.error('Keycloak account is ready but the email could not be sent.');
      } else {
        toast.success('Invitation sent.');
      }
      await queryClient.invalidateQueries({ queryKey: ['operators', username] });
    } catch (err) {
      const message = describeApiError(err, 'Failed to resend the invitation.');
      setServerError(message);
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  const onSavePermissions = async () => {
    if (!username || !hasPendingChanges) return;
    setServerError(null);
    setSavingPermissions(true);
    try {
      for (const change of pendingChanges) {
        await api.put(`/api/users/${username}/permissions`, {
          tenantCod: change.tenantCod,
          permissions: change.permissionCodes,
        });
      }
      toast.success('Permissions saved.');
      await queryClient.invalidateQueries({ queryKey: ['operators', username] });
      await queryClient.invalidateQueries({ queryKey: ['operators'] });
      // After invalidation the operator refetch will re-seed via the effect
      // above — but the effect's guard only fires when originalGrid is empty.
      // Drop it now so the seeded post-save grid becomes the new baseline.
      setOriginalGrid({});
    } catch (err) {
      const message = describeApiError(err, 'Failed to save permissions.');
      setServerError(message);
      toast.error(message);
    } finally {
      setSavingPermissions(false);
    }
  };

  const onToggleActive = async (nextIsActive: boolean) => {
    if (!username) return;
    setServerError(null);
    setTogglingActive(true);
    try {
      await api.put(`/api/users/${username}`, { isActive: nextIsActive });
      toast.success(nextIsActive ? 'Operator reactivated.' : 'Operator deactivated.');
      await queryClient.invalidateQueries({ queryKey: ['operators', username] });
      await queryClient.invalidateQueries({ queryKey: ['operators'] });
    } catch (err) {
      const message = describeApiError(
        err,
        nextIsActive ? 'Failed to reactivate operator.' : 'Failed to deactivate operator.',
      );
      setServerError(message);
      toast.error(message);
    } finally {
      setTogglingActive(false);
      setConfirmDeactivate(false);
    }
  };

  if (!username) {
    return <div className="p-6">Missing operator username.</div>;
  }

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => navigate('/admin/operators')}>
            ← Back to operators
          </Button>
        </div>

        {operatorQuery.isLoading && (
          <div className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-6 text-center text-sm text-[#666]">
            Loading operator…
          </div>
        )}

        {operatorQuery.error && (
          <div
            className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
            role="alert"
          >
            Failed to load operator.
          </div>
        )}

        {operator && (
          <>
            {/* Header card */}
            <div className="rounded-lg border border-[#e0e0e0] bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-xl font-semibold text-[#333]">{operator.username}</h1>
                  <p className="mt-1 text-[13px] text-[#666]">{operator.email}</p>
                  <p className="mt-2 text-[12px] text-[#888]">
                    Last login: {formatLastLogin(operator.lastLoginAt)}
                  </p>
                  {operator.createdBy && (
                    <p className="mt-1 text-[12px] text-[#888]">Created by {operator.createdBy}</p>
                  )}
                  {status !== 'active' && status !== 'inactive' && (
                    <p className="mt-1 text-[12px] text-[#888]">
                      {status === 'pending' && 'Keycloak account not created yet.'}
                      {status === 'not-invited' && 'Invitation not sent yet.'}
                      {status === 'invited' &&
                        `Invited on ${new Date(operator.invitedAt!).toLocaleString()} — has not logged in yet.`}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {status === 'active' && <Badge variant="success">Active</Badge>}
                  {status === 'inactive' && <Badge variant="error">Inactive</Badge>}
                  {status === 'invited' && <Badge variant="warning">Invited</Badge>}
                  {status === 'pending' && <Badge variant="error">Keycloak pending</Badge>}
                  {status === 'not-invited' && <Badge variant="warning">Not invited</Badge>}
                  {canResend && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={resending}
                      onClick={onResendInvitation}
                    >
                      {resending ? 'Sending…' : 'Resend invitation'}
                    </Button>
                  )}
                  {status === 'active' && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={togglingActive || isSelf}
                      title={isSelf ? 'Cannot deactivate yourself' : undefined}
                      onClick={() => setConfirmDeactivate(true)}
                    >
                      Deactivate operator
                    </Button>
                  )}
                  {status === 'inactive' && (
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={togglingActive}
                      onClick={() => onToggleActive(true)}
                    >
                      Reactivate operator
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Permission grid */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-[#333]">Permissions</h2>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!hasPendingChanges || gridLocked}
                  onClick={onSavePermissions}
                >
                  {savingPermissions
                    ? 'Saving…'
                    : hasPendingChanges
                      ? `Save permissions (${pendingChanges.length} tenant${
                          pendingChanges.length === 1 ? '' : 's'
                        })`
                      : 'Save permissions'}
                </Button>
              </div>

              {iCreatedThisOperator && (
                <div
                  className="rounded-lg border border-[#ffe0b2] bg-[#fff8e1] px-4 py-3 text-[13px] text-[#8a6d1a]"
                  role="note"
                >
                  You created this operator, so you cannot grant them permissions — another operator
                  with GRANT_PERMISSIONS must. Two people, always.
                </div>
              )}
              {!iCreatedThisOperator && meQuery.data && !canGrantAnywhere && (
                <div
                  className="rounded-lg border border-[#e0e0e0] bg-[#fafafa] px-4 py-3 text-[13px] text-[#555]"
                  role="note"
                >
                  You hold GRANT_PERMISSIONS on no tenant — read-only view.
                </div>
              )}
              {!iCreatedThisOperator && canGrantAnywhere && (
                <p className="text-[12px] text-[#666]">
                  You can only grant, on each tenant, the permissions you hold there yourself.
                  Greyed cells are outside your reach.
                </p>
              )}
              {(tenantsQuery.isLoading || permissionsQuery.isLoading || meQuery.isLoading) && (
                <div className="rounded-lg border border-[#e0e0e0] bg-white px-4 py-6 text-center text-sm text-[#666]">
                  Loading tenants and permissions…
                </div>
              )}
              {(tenantsQuery.error || permissionsQuery.error) && (
                <div
                  className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
                  role="alert"
                >
                  Failed to load tenants or permissions.
                </div>
              )}
              {meQuery.error && (
                <div
                  className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
                  role="alert"
                >
                  Could not load your own permissions — the grid cannot tell what you may grant.
                </div>
              )}
              {!tenantsQuery.isLoading &&
                !permissionsQuery.isLoading &&
                !meQuery.isLoading &&
                !tenantsQuery.error &&
                !permissionsQuery.error &&
                !meQuery.error && (
                  <PermissionGrid
                    tenants={tenants}
                    permissions={permissions}
                    value={grid}
                    onChange={setGrid}
                    disabled={gridLocked}
                    grantable={grantable}
                  />
                )}
            </div>

            {/* Server error */}
            {serverError && (
              <div
                className="rounded-lg border border-[#ffcdd2] bg-[#ffebee] px-4 py-3 text-sm text-[#c62828]"
                role="alert"
              >
                {serverError}
              </div>
            )}
          </>
        )}

        <ConfirmDialog
          isOpen={confirmDeactivate}
          title="Deactivate operator?"
          message={`Deactivating '${username}' immediately revokes their next refresh. Existing access tokens expire within the configured TTL (default 120s).`}
          confirmText="Deactivate"
          cancelText="Cancel"
          isDangerous
          onConfirm={() => onToggleActive(false)}
          onCancel={() => setConfirmDeactivate(false)}
        />
      </div>
    </ErrorBoundary>
  );
}
